import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import Label from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Button } from '../../ui/button';
import { Heart, Activity, Scale, Droplets, Zap, Pill, CheckCircle, Loader2, Clipboard } from 'lucide-react';
import { toast } from 'react-hot-toast';
import StableNumberInput from './stable-number-input';
import GlobalPasteModal from './global-paste-modal';

const VitalsLabsForm = ({ data, onChange, onSave, isLoading, readonly = false, prechartingData, onGlobalPasteApply }) => {
  const [formData, setFormData] = useState({
    bloodPressure: {
      systolic: '',
      diastolic: ''
    },
    weight: {
      before: '',
      after: ''
    },
    bmi: {
      before: '',
      after: ''
    },
    weightLoss: {
      value: '',
      duration: '',
      durationUnit: 'months'
    },
    glucose: {
      a1c: {
        before: '',
        after: ''
      },
      fbs: {
        before: '',
        after: ''
      }
    },
    kidney: {
      srCreat: {
        before: '',
        after: ''
      },
      egfr: '',
      urineAlbCreatRatio: {
        before: '',
        after: ''
      }
    },
    liver: {
      ast: {
        before: '',
        after: ''
      },
      alt: {
        before: '',
        after: ''
      },
      totProt: '',
      totBilirubin: '',
      srAlb: '',
      alp: ''
    },
    electrolytes: {
      na: '',
      k: '',
      cl: '',
      co2: '',
      ca: {
        before: '',
        after: ''
      }
    },
    lipidPanel: {
      totChol: {
        before: '',
        after: ''
      },
      ldl: {
        before: '',
        after: ''
      },
      hdl: {
        before: '',
        after: ''
      },
      tg: {
        before: '',
        after: ''
      }
    },
    cbc: {
      hb: {
        before: '',
        after: ''
      },
      hematocrit: '',
      rbc: '',
      mcv: '',
      wbc: '',
      platelets: ''
    },
    thyroid: {
      tsh: {
        before: '',
        after: ''
      },
      freeT3: {
        before: '',
        after: ''
      },
      freeT4: {
        before: '',
        after: ''
      }
    },
    parathyroid: {
      intactPTH: {
        before: '',
        after: ''
      },
      wholePTH: {
        before: '',
        after: ''
      },
      srPhosphorous: {
        before: '',
        after: ''
      }
    },
    ra: {
      rf: '',
      esr: '',
      crp: '',
      ana: ''
    }
  });
  const [showTextModal, setShowTextModal] = useState(false);

  const formDataRef = useRef(formData);
  const updateTimeoutRef = useRef(null);
  formDataRef.current = formData;
  const debouncedUpdate = useCallback((updatedData) => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    updateTimeoutRef.current = setTimeout(() => {
      if (onChange) {
        onChange(updatedData);
      }
    }, 100);
  }, [onChange]);

  useEffect(() => {
    if (data) {
      setFormData(prev => ({
        ...prev,
        ...data
      }));
    }
  }, [data]);

  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = useCallback((section, field, value) => {
    setFormData(prev => {
      const updatedData = {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      };
      debouncedUpdate(updatedData);
      
      return updatedData; 
    });
  }, [debouncedUpdate]);

  const handleBeforeAfterChange = useCallback((section, field, type, value) => {
    setFormData(prev => {
      const sectionData = prev[section] || {};
      if (field === type) {
        const updatedData = {
          ...prev,
          [section]: {
            ...sectionData,
            [field]: value
          }
        };
        debouncedUpdate(updatedData);
        return updatedData;
      }
      
      const fieldData = sectionData[field] || { before: '', after: '' };
      const updatedData = {
        ...prev,
        [section]: {
          ...sectionData,
          [field]: {
            ...fieldData,
            [type]: value
          }
        }
      };
      debouncedUpdate(updatedData);
      
      return updatedData;
    });
  }, [debouncedUpdate]);

  const handleBloodPressureChange = useCallback((field, value) => {
    if (field === 'systolic' && value.length === 3) {
      setTimeout(() => {
        const diastolicInput = document.getElementById('diastolic-bp');
        if (diastolicInput) {
          diastolicInput.focus();
        }
      }, 100);
    }
    
    handleInputChange('bloodPressure', field, value);
  }, [handleInputChange]);

  const validateForm = () => {
    const errors = [];
    if (formData.bloodPressure.systolic && formData.bloodPressure.diastolic) {
      const systolic = parseInt(formData.bloodPressure.systolic);
      const diastolic = parseInt(formData.bloodPressure.diastolic);
      
      if (systolic < 70 || systolic > 250) {
        errors.push('Systolic blood pressure should be between 70-250 mmHg');
      }
      if (diastolic < 40 || diastolic > 150) {
        errors.push('Diastolic blood pressure should be between 40-150 mmHg');
      }
      if (systolic <= diastolic) {
        errors.push('Systolic pressure should be higher than diastolic pressure');
      }
    }
    
    if (formData.bmi?.before) {
      const bmi = parseFloat(formData.bmi.before);
      if (bmi < 10 || bmi > 100) {
        errors.push('BMI (before) should be between 10-100');
      }
    }
    if (formData.bmi?.after) {
      const bmi = parseFloat(formData.bmi.after);
      if (bmi < 10 || bmi > 100) {
        errors.push('BMI (after) should be between 10-100');
      }
    }
    
    if (formData.weightLoss.value && formData.weightLoss.duration) {
      const value = parseFloat(formData.weightLoss.value);
      const duration = parseInt(formData.weightLoss.duration);
      
      if (value < 0 || value > 500) {
        errors.push('Weight loss value should be between 0-500 lbs');
      }
      if (duration < 1 || duration > 120) {
        errors.push('Weight loss duration should be between 1-120');
      }
    }
    
    return errors;
  };

  const hasData = () => {
    const checkValue = (value) => {
      if (Array.isArray(value)) {
        return value.length > 0 && value.some(item => checkValue(item));
      }
      if (typeof value === 'object' && value !== null) {
        return Object.values(value).some(val => checkValue(val));
      }
      return value !== null && value !== undefined && value !== '';
    };
    
    return checkValue(formData);
  };

  const handleSave = async () => {
    if (!hasData()) {
      toast.error('Please fill in at least one field before saving');
      return;
    }

    const errors = validateForm();
    if (errors.length > 0) {
      toast.error(errors.join(', '));
      return;
    }
    
    if (onSave) {
      await onSave(formData);
    }
  };

  return (
    <div className="space-y-6"> 
      {!readonly && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowTextModal(true)}
            className="flex items-center gap-2"
          >
            <Clipboard className="h-4 w-4" />
            Extract from Text
          </Button>
        </div>
      )}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-red-500" />
            Blood Pressure
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="systolic-bp">Systolic</Label>
              <div className="flex items-center gap-2">
                <StableNumberInput
                  key="systolic-bp"
                  id="systolic-bp"
                  value={formData.bloodPressure.systolic}
                  onChange={(value) => handleBloodPressureChange('systolic', value)}
                  placeholder="120"
                  min="70"
                  max="250"
                  step="1"
                  readonly={readonly}
                />
                <span className="text-sm text-gray-500">mmHg</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="diastolic-bp">Diastolic</Label>
              <div className="flex items-center gap-2">
                <StableNumberInput
                  key="diastolic-bp"
                  id="diastolic-bp"
                  value={formData.bloodPressure.diastolic}
                  onChange={(value) => handleBloodPressureChange('diastolic', value)}
                  placeholder="80"
                  min="40"
                  max="150"
                  step="1"
                  readonly={readonly}
                />
                <span className="text-sm text-gray-500">mmHg</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Scale className="h-5 w-5 text-blue-500" />
            Weight
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="weight-before">Before</Label>
              <div className="flex items-center gap-2">
                <StableNumberInput
                  key="weight-before"
                  id="weight-before"
                  value={formData.weight?.before || ''}
                  onChange={(value) => handleInputChange('weight', 'before', value)}
                  placeholder="180"
                  min="50"
                  max="500"
                  step="0.1"
                  readonly={readonly}
                />
                <span className="text-sm text-gray-500">lbs</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="weight-after">After</Label>
              <div className="flex items-center gap-2">
                <StableNumberInput
                  key="weight-after"
                  id="weight-after"
                  value={formData.weight?.after || ''}
                  onChange={(value) => handleInputChange('weight', 'after', value)}
                  placeholder="170"
                  min="50"
                  max="500"
                  step="0.1"
                  readonly={readonly}
                />
                <span className="text-sm text-gray-500">lbs</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Scale className="h-5 w-5 text-blue-500" />
            BMI
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="bmi-before">Before</Label>
              <div className="flex items-center gap-2">
                <StableNumberInput
                  key="bmi-before"
                  id="bmi-before"
                  value={formData.bmi?.before || ''}
                  onChange={(value) => handleInputChange('bmi', 'before', value)}
                  placeholder="28.5"
                  min="10"
                  max="100"
                  step="0.1"
                  readonly={readonly}
                />
                <span className="text-sm text-gray-500">kg/m²</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bmi-after">After</Label>
              <div className="flex items-center gap-2">
                <StableNumberInput
                  key="bmi-after"
                  id="bmi-after"
                  value={formData.bmi?.after || ''}
                  onChange={(value) => handleInputChange('bmi', 'after', value)}
                  placeholder="26.5"
                  min="10"
                  max="100"
                  step="0.1"
                  readonly={readonly}
                />
                <span className="text-sm text-gray-500">kg/m²</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-green-500" />
            Weight Loss
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="weight-loss-value">Weight Loss</Label>
              <div className="flex items-center gap-2">
                <StableNumberInput
                  id="weight-loss-value"
                  value={formData.weightLoss.value}
                  onChange={(value) => handleInputChange('weightLoss', 'value', value)}
                  placeholder="10"
                  min="0"
                  max="500"
                  step="0.1"
                  readonly={readonly}
                />
                <span className="text-sm text-gray-500">lbs</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="weight-loss-duration">Duration</Label>
              <div className="flex items-center gap-2">
                <StableNumberInput
                  id="weight-loss-duration"
                  value={formData.weightLoss.duration}
                  onChange={(value) => handleInputChange('weightLoss', 'duration', value)}
                  placeholder="3"
                  min="1"
                  max="120"
                  step="1"
                  readonly={readonly}
                />
                <span className="text-sm text-gray-500">{formData.weightLoss.durationUnit || 'months'}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="weight-loss-unit">Unit</Label>
              <Select
                value={formData.weightLoss.durationUnit}
                onValueChange={(value) => handleInputChange('weightLoss', 'durationUnit', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select unit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="days">day(s)</SelectItem>
                  <SelectItem value="weeks">week(s)</SelectItem>
                  <SelectItem value="months">month(s)</SelectItem>
                  <SelectItem value="years">year(s)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card> 
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Droplets className="h-5 w-5 text-yellow-500" />
            Glucose
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="a1c">A1c</Label>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label htmlFor="a1c-before">Before</Label>
                  <div className="flex items-center gap-2">
                    <StableNumberInput
                      id="a1c-before"
                      value={formData.glucose?.a1c?.before || ''}
                      onChange={(value) => handleBeforeAfterChange('glucose', 'a1c', 'before', value)}
                      placeholder="7.5"
                      min="3"
                      max="15"
                      step="0.1"
                      readonly={readonly}
                    />
                    <span className="text-sm text-gray-500">%</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="a1c-after">After</Label>
                  <div className="flex items-center gap-2">
                    <StableNumberInput
                      id="a1c-after"
                      value={formData.glucose?.a1c?.after || ''}
                      onChange={(value) => handleBeforeAfterChange('glucose', 'a1c', 'after', value)}
                      placeholder="6.2"
                      min="3"
                      max="15"
                      step="0.1"
                      readonly={readonly}
                    />
                    <span className="text-sm text-gray-500">%</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="fbs">FBS</Label>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label htmlFor="fbs-before">Before</Label>
                  <div className="flex items-center gap-2">
                    <StableNumberInput
                      id="fbs-before"
                      value={formData.glucose?.fbs?.before || ''}
                      onChange={(value) => handleBeforeAfterChange('glucose', 'fbs', 'before', value)}
                      placeholder="120"
                      min="50"
                      max="500"
                      step="1"
                      readonly={readonly}
                    />
                    <span className="text-sm text-gray-500">mg/dL</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fbs-after">After</Label>
                  <div className="flex items-center gap-2">
                    <StableNumberInput
                      id="fbs-after"
                      value={formData.glucose?.fbs?.after || ''}
                      onChange={(value) => handleBeforeAfterChange('glucose', 'fbs', 'after', value)}
                      placeholder="95"
                      min="50"
                      max="500"
                      step="1"
                      readonly={readonly}
                    />
                    <span className="text-sm text-gray-500">mg/dL</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-purple-500" />
            Kidney Function
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="sr-creat">Sr. Creat</Label>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label htmlFor="sr-creat-before">Before</Label>
                  <div className="flex items-center gap-2">
                    <StableNumberInput
                      id="sr-creat-before"
                      value={formData.kidney?.srCreat?.before || ''}
                      onChange={(value) => handleBeforeAfterChange('kidney', 'srCreat', 'before', value)}
                      placeholder="1.3"
                      min="0.1"
                      max="20"
                      step="0.1"
                      readonly={readonly}
                    />
                    <span className="text-sm text-gray-500">mg/dL</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sr-creat-after">After</Label>
                  <div className="flex items-center gap-2">
                    <StableNumberInput
                      id="sr-creat-after"
                      value={formData.kidney?.srCreat?.after || ''}
                      onChange={(value) => handleBeforeAfterChange('kidney', 'srCreat', 'after', value)}
                      placeholder="1.1"
                      min="0.1"
                      max="20"
                      step="0.1"
                      readonly={readonly}
                    />
                    <span className="text-sm text-gray-500">mg/dL</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="egfr">eGFR</Label>
              <div className="space-y-2">
                <div className="h-5"></div>
                <div className="flex items-center gap-2">
                  <StableNumberInput
                    id="egfr"
                    value={formData.kidney?.egfr || ''}
                    onChange={(value) => handleInputChange('kidney', 'egfr', value)}
                    placeholder="85"
                    min="5"
                    max="200"
                    step="1"
                    readonly={readonly}
                  />
                  <span className="text-sm text-gray-500">mL/min/1.73m²</span>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="urine-alb-creat">Urine Alb:Creat Ratio</Label>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label htmlFor="urine-alb-creat-before">Before</Label>
                  <div className="flex items-center gap-2">
                    <StableNumberInput
                      id="urine-alb-creat-before"
                      value={formData.kidney?.urineAlbCreatRatio?.before || ''}
                      onChange={(value) => handleBeforeAfterChange('kidney', 'urineAlbCreatRatio', 'before', value)}
                      placeholder="30"
                      min="0"
                      max="1000"
                      step="1"
                      readonly={readonly}
                    />
                    <span className="text-sm text-gray-500">mg/g</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="urine-alb-creat-after">After</Label>
                  <div className="flex items-center gap-2">
                    <StableNumberInput
                      id="urine-alb-creat-after"
                      value={formData.kidney?.urineAlbCreatRatio?.after || ''}
                      onChange={(value) => handleBeforeAfterChange('kidney', 'urineAlbCreatRatio', 'after', value)}
                      placeholder="15"
                      min="0"
                      max="1000"
                      step="1"
                      readonly={readonly}
                    />
                    <span className="text-sm text-gray-500">mg/g</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Pill className="h-5 w-5 text-indigo-500" />
            Additional Labs
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h4 className="font-medium mb-3">Liver Function</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ast">AST (SGOT)</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label htmlFor="ast-before">Before</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="ast-before"
                        value={formData.liver?.ast?.before || ''}
                        onChange={(value) => handleBeforeAfterChange('liver', 'ast', 'before', value)}
                        placeholder="35"
                        min="0"
                        max="1000"
                        step="1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">U/L</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ast-after">After</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="ast-after"
                        value={formData.liver?.ast?.after || ''}
                        onChange={(value) => handleBeforeAfterChange('liver', 'ast', 'after', value)}
                        placeholder="25"
                        min="0"
                        max="1000"
                        step="1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">U/L</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="alt">ALT (SGPT)</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label htmlFor="alt-before">Before</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="alt-before"
                        value={formData.liver?.alt?.before || ''}
                        onChange={(value) => handleBeforeAfterChange('liver', 'alt', 'before', value)}
                        placeholder="40"
                        min="0"
                        max="1000"
                        step="1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">U/L</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="alt-after">After</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="alt-after"
                        value={formData.liver?.alt?.after || ''}
                        onChange={(value) => handleBeforeAfterChange('liver', 'alt', 'after', value)}
                        placeholder="30"
                        min="0"
                        max="1000"
                        step="1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">U/L</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tot-prot">Tot. Prot</Label>
                <div className="space-y-2">
                  <div className="h-5"></div>
                  <div className="flex items-center gap-2">
                    <StableNumberInput
                      id="tot-prot"
                      value={formData.liver.totProt}
                      onChange={(value) => handleInputChange('liver', 'totProt', value)}
                      placeholder="7.2"
                      min="0"
                      max="20"
                      step="0.1"
                      readonly={readonly}
                    />
                    <span className="text-sm text-gray-500">g/dL</span>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tot-bilirubin">Tot Bilirubin</Label>
                <div className="space-y-2">
                  <div className="h-5"></div>
                  <div className="flex items-center gap-2">
                    <StableNumberInput
                      id="tot-bilirubin"
                      value={formData.liver.totBilirubin}
                      onChange={(value) => handleInputChange('liver', 'totBilirubin', value)}
                      placeholder="0.8"
                      min="0"
                      max="50"
                      step="0.1"
                      readonly={readonly}
                    />
                    <span className="text-sm text-gray-500">mg/dL</span>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sr-alb">Sr. Alb</Label>
                <div className="space-y-2">
                  <div className="h-5"></div>
                  <div className="flex items-center gap-2">
                    <StableNumberInput
                      id="sr-alb"
                      value={formData.liver.srAlb}
                      onChange={(value) => handleInputChange('liver', 'srAlb', value)}
                      placeholder="4.2"
                      min="0"
                      max="10"
                      step="0.1"
                      readonly={readonly}
                    />
                    <span className="text-sm text-gray-500">g/dL</span>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="alp">ALP</Label>
                <div className="space-y-2">
                  <div className="h-5"></div>
                  <div className="flex items-center gap-2">
                    <StableNumberInput
                      id="alp"
                      value={formData.liver.alp}
                      onChange={(value) => handleInputChange('liver', 'alp', value)}
                      placeholder="75"
                      min="0"
                      max="1000"
                      step="1"
                      readonly={readonly}
                    />
                    <span className="text-sm text-gray-500">U/L</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-medium mb-3">Electrolytes</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label htmlFor="na">Na+</Label>
                <div className="space-y-2">
                  <div className="h-5"></div>
                  <div className="flex items-center gap-2">
                    <StableNumberInput
                      id="na"
                      value={formData.electrolytes.na}
                      onChange={(value) => handleInputChange('electrolytes', 'na', value)}
                      placeholder="140"
                      min="100"
                      max="200"
                      step="1"
                      readonly={readonly}
                    />
                    <span className="text-sm text-gray-500">mEq/L</span>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="k">K+</Label>
                <div className="space-y-2">
                  <div className="h-5"></div>
                  <div className="flex items-center gap-2">
                    <StableNumberInput
                      id="k"
                      value={formData.electrolytes.k}
                      onChange={(value) => handleInputChange('electrolytes', 'k', value)}
                      placeholder="4.2"
                      min="1"
                      max="10"
                      step="0.1"
                      readonly={readonly}
                    />
                    <span className="text-sm text-gray-500">mEq/L</span>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cl">Cl-</Label>
                <div className="space-y-2">
                  <div className="h-5"></div>
                  <div className="flex items-center gap-2">
                    <StableNumberInput
                      id="cl"
                      value={formData.electrolytes.cl}
                      onChange={(value) => handleInputChange('electrolytes', 'cl', value)}
                      placeholder="102"
                      min="80"
                      max="150"
                      step="1"
                      readonly={readonly}
                    />
                    <span className="text-sm text-gray-500">mEq/L</span>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="co2">CO2-</Label>
                <div className="space-y-2">
                  <div className="h-5"></div>
                  <div className="flex items-center gap-2">
                    <StableNumberInput
                      id="co2"
                      value={formData.electrolytes.co2}
                      onChange={(value) => handleInputChange('electrolytes', 'co2', value)}
                      placeholder="24"
                      min="10"
                      max="50"
                      step="1"
                      readonly={readonly}
                    />
                    <span className="text-sm text-gray-500">mEq/L</span>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ca">Ca+2</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label htmlFor="ca-before">Before</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="ca-before"
                        value={formData.electrolytes?.ca?.before || ''}
                        onChange={(value) => handleBeforeAfterChange('electrolytes', 'ca', 'before', value)}
                        placeholder="10.2"
                        min="5"
                        max="15"
                        step="0.1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">mg/dL</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ca-after">After</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="ca-after"
                        value={formData.electrolytes?.ca?.after || ''}
                        onChange={(value) => handleBeforeAfterChange('electrolytes', 'ca', 'after', value)}
                        placeholder="9.8"
                        min="5"
                        max="15"
                        step="0.1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">mg/dL</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-medium mb-3">Lipid Panel</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tot-chol">Tot Chol</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label htmlFor="tot-chol-before">Before</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="tot-chol-before"
                        value={formData.lipidPanel?.totChol?.before || ''}
                        onChange={(value) => handleBeforeAfterChange('lipidPanel', 'totChol', 'before', value)}
                        placeholder="220"
                        min="50"
                        max="500"
                        step="1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">mg/dL</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tot-chol-after">After</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="tot-chol-after"
                        value={formData.lipidPanel?.totChol?.after || ''}
                        onChange={(value) => handleBeforeAfterChange('lipidPanel', 'totChol', 'after', value)}
                        placeholder="180"
                        min="50"
                        max="500"
                        step="1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">mg/dL</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ldl">LDL</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label htmlFor="ldl-before">Before</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="ldl-before"
                        value={formData.lipidPanel?.ldl?.before || ''}
                        onChange={(value) => handleBeforeAfterChange('lipidPanel', 'ldl', 'before', value)}
                        placeholder="140"
                        min="20"
                        max="300"
                        step="1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">mg/dL</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ldl-after">After</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="ldl-after"
                        value={formData.lipidPanel?.ldl?.after || ''}
                        onChange={(value) => handleBeforeAfterChange('lipidPanel', 'ldl', 'after', value)}
                        placeholder="110"
                        min="20"
                        max="300"
                        step="1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">mg/dL</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="hdl">HDL</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label htmlFor="hdl-before">Before</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="hdl-before"
                        value={formData.lipidPanel?.hdl?.before || ''}
                        onChange={(value) => handleBeforeAfterChange('lipidPanel', 'hdl', 'before', value)}
                        placeholder="40"
                        min="10"
                        max="150"
                        step="1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">mg/dL</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="hdl-after">After</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="hdl-after"
                        value={formData.lipidPanel?.hdl?.after || ''}
                        onChange={(value) => handleBeforeAfterChange('lipidPanel', 'hdl', 'after', value)}
                        placeholder="45"
                        min="10"
                        max="150"
                        step="1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">mg/dL</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tg">TG</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label htmlFor="tg-before">Before</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="tg-before"
                        value={formData.lipidPanel?.tg?.before || ''}
                        onChange={(value) => handleBeforeAfterChange('lipidPanel', 'tg', 'before', value)}
                        placeholder="180"
                        min="20"
                        max="1000"
                        step="1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">mg/dL</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tg-after">After</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="tg-after"
                        value={formData.lipidPanel?.tg?.after || ''}
                        onChange={(value) => handleBeforeAfterChange('lipidPanel', 'tg', 'after', value)}
                        placeholder="125"
                        min="20"
                        max="1000"
                        step="1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">mg/dL</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-medium mb-3">CBC</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="hb">Hb</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label htmlFor="hb-before">Before</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="hb-before"
                        value={formData.cbc?.hb?.before || ''}
                        onChange={(value) => handleBeforeAfterChange('cbc', 'hb', 'before', value)}
                        placeholder="12.5"
                        min="5"
                        max="25"
                        step="0.1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">g/dL</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="hb-after">After</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="hb-after"
                        value={formData.cbc?.hb?.after || ''}
                        onChange={(value) => handleBeforeAfterChange('cbc', 'hb', 'after', value)}
                        placeholder="14.5"
                        min="5"
                        max="25"
                        step="0.1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">g/dL</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="hematocrit">Hematocrit</Label>
                <div className="space-y-2">
                  <div className="h-5"></div>
                  <div className="flex items-center gap-2">
                    <StableNumberInput
                      id="hematocrit"
                      value={formData.cbc.hematocrit}
                      onChange={(value) => handleInputChange('cbc', 'hematocrit', value)}
                      placeholder="42"
                      min="15"
                      max="70"
                      step="0.1"
                      readonly={readonly}
                    />
                    <span className="text-sm text-gray-500">%</span>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="rbc">RBC</Label>
                <div className="space-y-2">
                  <div className="h-5"></div>
                  <div className="flex items-center gap-2">
                    <StableNumberInput
                      id="rbc"
                      value={formData.cbc.rbc}
                      onChange={(value) => handleInputChange('cbc', 'rbc', value)}
                      placeholder="4.5"
                      min="2"
                      max="8"
                      step="0.1"
                      readonly={readonly}
                    />
                    <span className="text-sm text-gray-500">M/μL</span>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="mcv">MCV</Label>
                <div className="space-y-2">
                  <div className="h-5"></div>
                  <div className="flex items-center gap-2">
                    <StableNumberInput
                      id="mcv"
                      value={formData.cbc.mcv}
                      onChange={(value) => handleInputChange('cbc', 'mcv', value)}
                      placeholder="90"
                      min="50"
                      max="150"
                      step="1"
                      readonly={readonly}
                    />
                    <span className="text-sm text-gray-500">fL</span>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="wbc">WBC</Label>
                <div className="space-y-2">
                  <div className="h-5"></div>
                  <div className="flex items-center gap-2">
                    <StableNumberInput
                      id="wbc"
                      value={formData.cbc.wbc}
                      onChange={(value) => handleInputChange('cbc', 'wbc', value)}
                      placeholder="7500"
                      min="1000"
                      max="50000"
                      step="100"
                      readonly={readonly}
                    />
                    <span className="text-sm text-gray-500">/μL</span>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="platelets">Platelets</Label>
                <div className="space-y-2">
                  <div className="h-5"></div>
                  <div className="flex items-center gap-2">
                    <StableNumberInput
                      id="platelets"
                      value={formData.cbc.platelets}
                      onChange={(value) => handleInputChange('cbc', 'platelets', value)}
                      placeholder="250000"
                      min="50000"
                      max="1000000"
                      step="1000"
                      readonly={readonly}
                    />
                    <span className="text-sm text-gray-500">/μL</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-medium mb-3">Thyroid</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tsh">TSH</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label htmlFor="tsh-before">Before</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="tsh-before"
                        value={formData.thyroid?.tsh?.before || ''}
                        onChange={(value) => handleBeforeAfterChange('thyroid', 'tsh', 'before', value)}
                        placeholder="5.5"
                        min="0"
                        max="100"
                        step="0.1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">mIU/L</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tsh-after">After</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="tsh-after"
                        value={formData.thyroid?.tsh?.after || ''}
                        onChange={(value) => handleBeforeAfterChange('thyroid', 'tsh', 'after', value)}
                        placeholder="2.5"
                        min="0"
                        max="100"
                        step="0.1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">mIU/L</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="free-t3">Free T3</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label htmlFor="free-t3-before">Before</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="free-t3-before"
                        value={formData.thyroid?.freeT3?.before || ''}
                        onChange={(value) => handleBeforeAfterChange('thyroid', 'freeT3', 'before', value)}
                        placeholder="2.8"
                        min="0"
                        max="20"
                        step="0.1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">pg/mL</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="free-t3-after">After</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="free-t3-after"
                        value={formData.thyroid?.freeT3?.after || ''}
                        onChange={(value) => handleBeforeAfterChange('thyroid', 'freeT3', 'after', value)}
                        placeholder="3.2"
                        min="0"
                        max="20"
                        step="0.1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">pg/mL</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="free-t4">Free T4</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label htmlFor="free-t4-before">Before</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="free-t4-before"
                        value={formData.thyroid?.freeT4?.before || ''}
                        onChange={(value) => handleBeforeAfterChange('thyroid', 'freeT4', 'before', value)}
                        placeholder="1.1"
                        min="0"
                        max="10"
                        step="0.1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">ng/dL</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="free-t4-after">After</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="free-t4-after"
                        value={formData.thyroid?.freeT4?.after || ''}
                        onChange={(value) => handleBeforeAfterChange('thyroid', 'freeT4', 'after', value)}
                        placeholder="1.4"
                        min="0"
                        max="10"
                        step="0.1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">ng/dL</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Parathyroid */}
          <div>
            <h4 className="font-medium mb-3">Parathyroid</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="intact-pth">Intact/Active PTH</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label htmlFor="intact-pth-before">Before</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="intact-pth-before"
                        value={formData.parathyroid?.intactPTH?.before || ''}
                        onChange={(value) => handleBeforeAfterChange('parathyroid', 'intactPTH', 'before', value)}
                        placeholder="65"
                        min="0"
                        max="500"
                        step="1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">pg/mL</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="intact-pth-after">After</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="intact-pth-after"
                        value={formData.parathyroid?.intactPTH?.after || ''}
                        onChange={(value) => handleBeforeAfterChange('parathyroid', 'intactPTH', 'after', value)}
                        placeholder="45"
                        min="0"
                        max="500"
                        step="1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">pg/mL</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="whole-pth">Whole PTH</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label htmlFor="whole-pth-before">Before</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="whole-pth-before"
                        value={formData.parathyroid?.wholePTH?.before || ''}
                        onChange={(value) => handleBeforeAfterChange('parathyroid', 'wholePTH', 'before', value)}
                        placeholder="55"
                        min="0"
                        max="500"
                        step="1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">pg/mL</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="whole-pth-after">After</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="whole-pth-after"
                        value={formData.parathyroid?.wholePTH?.after || ''}
                        onChange={(value) => handleBeforeAfterChange('parathyroid', 'wholePTH', 'after', value)}
                        placeholder="40"
                        min="0"
                        max="500"
                        step="1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">pg/mL</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sr-phosphorous">Sr. Phosphorous</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label htmlFor="sr-phosphorous-before">Before</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="sr-phosphorous-before"
                        value={formData.parathyroid?.srPhosphorous?.before || ''}
                        onChange={(value) => handleBeforeAfterChange('parathyroid', 'srPhosphorous', 'before', value)}
                        placeholder="4.2"
                        min="0"
                        max="20"
                        step="0.1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">mg/dL</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sr-phosphorous-after">After</Label>
                    <div className="flex items-center gap-2">
                      <StableNumberInput
                        id="sr-phosphorous-after"
                        value={formData.parathyroid?.srPhosphorous?.after || ''}
                        onChange={(value) => handleBeforeAfterChange('parathyroid', 'srPhosphorous', 'after', value)}
                        placeholder="3.5"
                        min="0"
                        max="20"
                        step="0.1"
                        readonly={readonly}
                      />
                      <span className="text-sm text-gray-500">mg/dL</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RA Tests */}
          <div>
            <h4 className="font-medium mb-3">RA Tests</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rf">RF</Label>
                <div className="flex items-center gap-2">
                  <StableNumberInput
                    id="rf"
                    value={formData.ra.rf}
                    onChange={(value) => handleInputChange('ra', 'rf', value)}
                    placeholder="15"
                    min="0"
                    max="1000"
                    step="1"
                  />
                  <span className="text-sm text-gray-500">IU/mL</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="esr">ESR</Label>
                <div className="flex items-center gap-2">
                  <StableNumberInput
                    id="esr"
                    value={formData.ra.esr}
                    onChange={(value) => handleInputChange('ra', 'esr', value)}
                    placeholder="20"
                    min="0"
                    max="200"
                    step="1"
                  />
                  <span className="text-sm text-gray-500">mm/hr</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="crp">CRP</Label>
                <div className="flex items-center gap-2">
                  <StableNumberInput
                    id="crp"
                    value={formData.ra.crp}
                    onChange={(value) => handleInputChange('ra', 'crp', value)}
                    placeholder="2.1"
                    min="0"
                    max="100"
                    step="0.1"
                  />
                  <span className="text-sm text-gray-500">mg/L</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ana">ANA</Label>
                <Input
                  id="ana"
                  value={formData.ra.ana}
                  onChange={(e) => handleInputChange('ra', 'ana', e.target.value)}
                  placeholder="negative"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      {!readonly && onSave && (
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={isLoading || readonly || !hasData()}
            className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Save Vitals & Labs
              </>
            )}
          </Button>
        </div>
      )}
      <GlobalPasteModal
        isOpen={showTextModal}
        onClose={() => setShowTextModal(false)}
        sectionKey="vitalsLabs"
        prechartingData={prechartingData}
        onApply={onGlobalPasteApply}
        readonly={readonly}
      />
    </div>
  );
};

export default React.memo(VitalsLabsForm);
